/**
 * Converts SVG string to PNG format compatible with jsPDF addImage()
 * @param {string} svgString - The SVG content as a string
 * @param {number} width - Desired width of the output PNG
 * @param {number} height - Desired height of the output PNG
 * @returns {Promise<Object>} PNG data object for use with pdf.addImage()
 */
export const convertSvgToPng = async (svgString, width, height) => {
  return new Promise((resolve, reject) => {
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Create an image element
      const img = new Image();
      
      // Create a blob from the SVG string
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        // Draw the image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert canvas to PNG data URL
        const pngDataUrl = canvas.toDataURL('image/png');
        
        // Clean up
        URL.revokeObjectURL(url);
        
        // Return object compatible with jsPDF addImage()
        resolve({
          imageData: pngDataUrl,
          format: 'PNG',
          width: width,
          height: height
        });
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(new Error(`Failed to load SVG: ${error.message || 'Unknown error'}`));
      };

      img.src = url;
    } catch (error) {
      reject(new Error(`SVG to PNG conversion failed: ${error.message}`));
    }
  });
};

/**
 * Helper function to load SVG from a file path
 * @param {string} svgPath - Path to the SVG file
 * @returns {Promise<string>} SVG content as string
 */
export const loadSvgFromPath = async (svgPath) => {
  try {
    const response = await fetch(svgPath);
    if (!response?.ok) {
      throw new Error(`Failed to load SVG from ${svgPath}`);
    }
    return await response?.text();
  } catch (error) {
    throw new Error(`Error loading SVG: ${error.message}`);
  }
};

/**
 * Convenience function that loads SVG from path and converts to PNG
 * @param {string} svgPath - Path to the SVG file
 * @param {number} width - Desired width of the output PNG
 * @param {number} height - Desired height of the output PNG
 * @returns {Promise<Object>} PNG data object for use with pdf.addImage()
 */
export const convertSvgFileToPng = async (svgPath, width, height) => {
  const svgString = await loadSvgFromPath(svgPath);
  return convertSvgToPng(svgString, width, height);
};

export default convertSvgToPng;